import { ConfigProvider, Flex, List, Popover, Space, Table, theme, Typography } from 'antd';
import { type ReactNode, useMemo } from 'react';
import { type SiteMenuOrderView } from '../../../../../entities/site-menu-order';
import {
  isRouteSubtotal,
  type RouteSubtotalRow,
  routeSubtotalRowClassName,
  withRouteSubtotalCells,
  withRouteSubtotals,
} from '../../../../../utils/order-utils';
import { type EditableSiteMenuOrder } from '../customer-order-form.utils';
import type { TableColumnsType } from 'antd';

const MEALS_LABEL_COLUMN_INDEX = 0;
const MEALS_QUANTITY_COLUMN_INDEX = 2;
const MEALS_BEVERAGES_COLUMN_INDEX = 3;

const mealLabel = (siteMenuOrder: EditableSiteMenuOrder): string =>
  `${siteMenuOrder.scheduledMeal.mealCategory.name} (${siteMenuOrder.mainType})`;

const beveragesAndSuppliesCount = (siteMenuOrder: EditableSiteMenuOrder): number =>
  (siteMenuOrder.siteMenuBeverageOrders ?? []).reduce(
    (sum, order) => sum + (order.quantity ?? 0),
    0
  ) +
  (siteMenuOrder.siteMenuSupplyOrders ?? []).reduce((sum, order) => sum + (order.quantity ?? 0), 0);

const routeSubtotalAmounts = {
  quantity: (row: SiteMenuOrderView) => row.quantity ?? 0,
  beveragesAndSupplies: (row: SiteMenuOrderView) =>
    beveragesAndSuppliesCount(row as unknown as EditableSiteMenuOrder),
};

const SortedValues = ({
  title,
  values,
  emptyText,
}: {
  title: string;
  values: string[] | null;
  emptyText: string;
}): ReactNode => {
  const { token } = theme.useToken();
  const style = { paddingInlineStart: token.marginXS };
  return (
    <>
      <Typography.Title level={5}>{title}</Typography.Title>
      {values?.toSorted().map((value) => (
        <Typography.Text style={style} key={value}>
          {value}
        </Typography.Text>
      )) ?? <Typography.Text style={style}>{emptyText}</Typography.Text>}
    </>
  );
};

const BeveragesAndSupplies = ({
  siteMenuOrder,
}: {
  siteMenuOrder: EditableSiteMenuOrder;
}): ReactNode => {
  const { token } = theme.useToken();
  const items = [
    ...(siteMenuOrder.siteMenuBeverageOrders ?? []).map((order) => ({
      name: order.beverage?.name,
      quantity: order.quantity ?? 0,
    })),
    ...(siteMenuOrder.siteMenuSupplyOrders ?? []).map((order) => ({
      name: order.supply?.name,
      quantity: order.quantity ?? 0,
    })),
  ];
  return (
    <ConfigProvider renderEmpty={() => 'No Beverages or Supplies'}>
      <List
        size="small"
        dataSource={items}
        renderItem={(item) => (
          <List.Item>
            <Typography.Text>{item.name}</Typography.Text>
            <Typography.Text style={{ marginLeft: token.marginSM }}>
              {item.quantity}
            </Typography.Text>
          </List.Item>
        )}
      />
    </ConfigProvider>
  );
};

interface SiteMenuOrdersTableProps {
  siteMenuOrders: EditableSiteMenuOrder[];
  /** The site menu order being edited or deleted, which is highlighted */
  selectedSiteMenuOrderId: string | undefined;
  onEdit: (siteMenuOrder: EditableSiteMenuOrder) => void;
  onDelete: (siteMenuOrder: EditableSiteMenuOrder) => void;
}

/** The site menu orders of a site, with a subtotal for each route */
const SiteMenuOrdersTable = ({
  siteMenuOrders,
  selectedSiteMenuOrderId,
  onEdit,
  onDelete,
}: SiteMenuOrdersTableProps): ReactNode => {
  const columns = useMemo(() => {
    const siteMenuOrderTableColumns: TableColumnsType<EditableSiteMenuOrder> = [
      {
        title: 'Meal',
        key: 'name',
        render: (_, siteMenuOrder) => <Typography.Text>{mealLabel(siteMenuOrder)}</Typography.Text>,
      },
      {
        title: 'Route',
        key: 'route',
        render: (_, siteMenuOrder) => (
          <Typography.Text>{siteMenuOrder.route?.name ?? 'N/A'}</Typography.Text>
        ),
      },
      {
        title: 'Quantity',
        key: 'quantity',
        render: (_, siteMenuOrder) => (
          <Popover
            content={
              <Flex vertical>
                <SortedValues
                  title="SpecialDiets"
                  values={siteMenuOrder.diets}
                  emptyText="No Special Diets"
                />
                <SortedValues
                  title="Preferences"
                  values={siteMenuOrder.preferences}
                  emptyText="No Preferences"
                />
              </Flex>
            }
          >
            {siteMenuOrder.quantity}
          </Popover>
        ),
      },
      {
        title: 'Beverages & Supplies',
        key: 'beveragesAndSupplies',
        render: (_, siteMenuOrder) => (
          <Popover
            content={<BeveragesAndSupplies siteMenuOrder={siteMenuOrder} />}
            title={`Beverages & Supplies (Beverages ${
              siteMenuOrder.prepackBeverages ? 'Prepacked' : 'NOT Prepacked'
            })`}
          >
            <Typography.Text>{beveragesAndSuppliesCount(siteMenuOrder)}</Typography.Text>
          </Popover>
        ),
      },
      {
        title: 'Action',
        key: 'action',
        render: (_, siteMenuOrder) => (
          <Space size="middle">
            <a
              onClick={() => {
                onEdit(siteMenuOrder);
              }}
            >
              Edit
            </a>
            <a
              onClick={() => {
                onDelete(siteMenuOrder);
              }}
            >
              Delete
            </a>
          </Space>
        ),
      },
    ];
    return withRouteSubtotalCells(
      siteMenuOrderTableColumns as unknown as TableColumnsType<SiteMenuOrderView>,
      MEALS_LABEL_COLUMN_INDEX,
      {
        [MEALS_QUANTITY_COLUMN_INDEX]: 'quantity',
        [MEALS_BEVERAGES_COLUMN_INDEX]: 'beveragesAndSupplies',
      }
    );
  }, [onEdit, onDelete]);

  const rows = useMemo(
    () =>
      withRouteSubtotals(siteMenuOrders as unknown as SiteMenuOrderView[], routeSubtotalAmounts),
    [siteMenuOrders]
  );

  return (
    <ConfigProvider renderEmpty={() => 'No meals'}>
      <Table
        columns={columns}
        rowKey={(row) =>
          isRouteSubtotal(row)
            ? (row as unknown as RouteSubtotalRow).rowKey
            : (row as unknown as EditableSiteMenuOrder).temporaryId
        }
        rowClassName={routeSubtotalRowClassName}
        dataSource={rows}
        size="small"
        pagination={false}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: selectedSiteMenuOrderId ? [selectedSiteMenuOrderId] : [],
          columnWidth: 0,
          renderCell: () => null,
        }}
        style={{ width: '100%' }}
      />
    </ConfigProvider>
  );
};

export default SiteMenuOrdersTable;
